/**
 * OptionsSection
 *
 * Checkbox group for:
 *   - LLM refinement  (default ON)
 *   - Karaoke mode    (default OFF; disabled when ASS is not in selected formats)
 */

import { memo } from 'react';
import { Sparkles, Music } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';

export const OptionsSection = memo(function OptionsSection() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleLlmRefinementChange, handleKaraokeChange } =
    useSubtitles();
  const { useLlmRefinement, useKaraoke, formats } = state;

  const interactionDisabled = isUploading || isGenerating;
  const assEnabled = formats.includes('ass');

  return (
    <div className="panel-glass">
      <h3 className="section-heading">
        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
        <span>{t('subtitles.options.title', { defaultValue: 'Options' })}</span>
      </h3>

      <div className="space-y-3">
        {/* LLM Refinement */}
        <label
          className={[
            'flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer',
            useLlmRefinement
              ? 'border-purple-500/40 bg-purple-900/15'
              : 'border-white/10 bg-black/20',
            interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-purple-500/30',
          ].join(' ')}
        >
          <input
            type="checkbox"
            checked={useLlmRefinement}
            disabled={interactionDisabled}
            onChange={(e) => handleLlmRefinementChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-purple-500 flex-shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-400 flex-shrink-0" />
              <span className="text-sm font-medium text-white">
                {t('subtitles.options.llmRefinement', { defaultValue: 'LLM Refinement' })}
              </span>
            </div>
            <p className="text-xs text-white/50 mt-0.5">
              {t('subtitles.options.llmRefinementHint', {
                defaultValue:
                  'Uses AI to improve translation quality — fixes context, pronouns and natural phrasing.',
              })}
            </p>
          </div>
        </label>

        {/* Karaoke */}
        <label
          className={[
            'flex items-start gap-3 p-3 rounded-lg border transition-colors',
            !assEnabled || interactionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            useKaraoke && assEnabled
              ? 'border-pink-500/40 bg-pink-900/15'
              : 'border-white/10 bg-black/20',
            assEnabled && !interactionDisabled ? 'hover:border-pink-500/30' : '',
          ].join(' ')}
          title={
            !assEnabled
              ? t('subtitles.options.karaokeOnlyAssHint', {
                  defaultValue: 'Karaoke is only available when ASS format is selected.',
                })
              : undefined
          }
        >
          <input
            type="checkbox"
            checked={useKaraoke && assEnabled}
            disabled={interactionDisabled || !assEnabled}
            onChange={(e) => handleKaraokeChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-pink-500 flex-shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <Music className="h-4 w-4 text-pink-400 flex-shrink-0" />
              <span className="text-sm font-medium text-white">
                {t('subtitles.options.karaoke', { defaultValue: 'Karaoke (word highlight)' })}
              </span>
              {!assEnabled && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/40">
                  {t('subtitles.options.karaokeOnlyAssHint', { defaultValue: 'Needs ASS' })}
                </span>
              )}
            </div>
            <p className="text-xs text-white/50 mt-0.5">
              {t('subtitles.options.karaokeDescription', {
                defaultValue:
                  'Highlights each word as it is spoken. Only affects the ASS subtitle file.',
              })}
            </p>
          </div>
        </label>
      </div>
    </div>
  );
});
